import { Role } from './roles.js'

/**
 * Hierarchy-aware authorization scope (TO-388): over which organizational
 * units a permission applies, relative to the anchor unit(s) a user is placed
 * in. Expansion walks the org-unit hierarchy index — never unit codes.
 */
export enum Scope {
	/** The anchor units themselves. */
	SELF = 'SELF',
	/** Units whose parent is an anchor (depth exactly 1). */
	CHILDREN = 'CHILDREN',
	/** Every unit below an anchor, at any depth (depth ≥ 1). */
	DESCENDANTS = 'DESCENDANTS',
	/** The anchors plus every unit below them. */
	SELF_AND_DESCENDANTS = 'SELF_AND_DESCENDANTS',
	/** The whole tenant; no unit restriction. */
	TENANT = 'TENANT',
}

/** Default scope of each built-in role. A role not listed here contributes no unit (fail-closed). */
export const ROLE_DEFAULT_SCOPE: Record<Role, Scope> = {
	[Role.ORG_USER]: Scope.SELF,
	[Role.ORG_MANAGER]: Scope.SELF_AND_DESCENDANTS,
	[Role.ORG_ADMIN]: Scope.TENANT,
	[Role.PLATFORM_ADMIN]: Scope.TENANT,
	[Role.SUPER_ADMIN]: Scope.TENANT,
}

/** Discriminator of a principal's resolved {@link PrincipalScope}. */
export enum PrincipalScopeKind {
	TENANT = 'TENANT',
	UNITS = 'UNITS',
}

/**
 * A principal's resolved scope: the whole tenant, or a concrete set of unit ids
 * of the principal's tenant. `anchorUnitIds` are the units of the user's current
 * organization assignments; `unitIds` is the permitted set. Both sorted, unique.
 */
export type PrincipalScope =
	| { kind: PrincipalScopeKind.TENANT }
	| { kind: PrincipalScopeKind.UNITS; anchorUnitIds: string[]; unitIds: string[] }
